import { createContext, useContext, useEffect, useState, useRef } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { getDepartmentByEmail } from "../utils/getDepartmentByEmail";
import { isSuperAdminEmail } from "../utils/superAdminEmails";

const AuthContext = createContext();

// Expose the signed-in user to the Cynet Connect SDK (see public/index.html).
function publishCynetUser(user) {
  window.__cynetUser = user && user.email ? { email: user.email } : null;
  window.dispatchEvent(new Event("cynet:user"));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userEmail = user?.email;
  useEffect(() => publishCynetUser(userEmail ? { email: userEmail } : null), [userEmail]);
  const bootstrappingRef = useRef(false);
  const allowedDomains = [
    "@cynetcorp.com",
    "@cynetlocums.com",
    "@cynethealth.com",
    "@cynethealth.ca",
    "@cynetsystems.com",
    "@cynetlocums.ca",
    "@cynetsystems.ca",
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        localStorage.removeItem("user");
        setLoading(false);
        return;
      }

      const { email, uid, displayName, photoURL } = firebaseUser;
      const normalizedEmail = String(email || "").toLowerCase().trim();
      const isSSOSignIn = (firebaseUser.providerData || []).some(
        (p) => p && p.providerId === "google.com"
      );
      const isAllowedDomain = allowedDomains.some((domain) =>
        normalizedEmail.endsWith(domain)
      );

      // Fast fail (no expensive network calls)
      if (!isSSOSignIn || !isAllowedDomain) {
        signOut(auth).catch(() => {});
        setUser(null);
        localStorage.removeItem("user");
        if (!isSSOSignIn) localStorage.setItem("auth_message", "SSO_REQUIRED");
        setLoading(false);
        return;
      }

      // Show app shell immediately using cached/minimal profile.
      const cachedUserRaw = localStorage.getItem("user");
      let cachedUser = null;
      try {
        cachedUser = cachedUserRaw ? JSON.parse(cachedUserRaw) : null;
      } catch {
        cachedUser = null;
      }
      const quickUser =
        cachedUser && cachedUser.email === email
          ? { uid, email, displayName, photo: photoURL, ...cachedUser }
          : {
              uid,
              email,
              displayName,
              photo: photoURL,
              role: isSuperAdminEmail(email) ? "super_admin" : "user",
            };
      setUser(quickUser);
      localStorage.setItem("user", JSON.stringify(quickUser));
      setLoading(false);

      // Run full profile bootstrap in background.
      bootstrapUser(firebaseUser);
    });

    return () => unsubscribe();
  }, []);

  const bootstrapUser = async (firebaseUser) => {
    if (bootstrappingRef.current) return;
    bootstrappingRef.current = true;

    const { email, displayName, uid, photoURL } = firebaseUser;

    try {
      const forcedRole = isSuperAdminEmail(email) ? "super_admin" : undefined;
      const userRef = doc(db, "workspaces", "ai-apps-dashboard", "users", uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() : {};
      const finalRole = forcedRole || data.role || "user";
      const finalUser = {
        uid,
        email,
        displayName,
        photo: photoURL,
        ...data,
        role: finalRole,
      };
      setUser(finalUser);
      localStorage.setItem("user", JSON.stringify(finalUser));

      // Expensive department lookup only when needed; keep it background.
      let resolvedDepartment = data.department || null;
      if (!resolvedDepartment) {
        const { department: departmentFromBigQuery } = await getDepartmentByEmail(email);
        resolvedDepartment = departmentFromBigQuery || null;
      }

      const updatePayload = {
        ...(resolvedDepartment != null ? { department: resolvedDepartment } : {}),
        ...(forcedRole ? { role: forcedRole } : {}),
        updatedAt: serverTimestamp(),
      };
      await setDoc(userRef, updatePayload, { merge: true });

      const mergedUser = { ...finalUser, ...updatePayload };
      setUser(mergedUser);
      localStorage.setItem("user", JSON.stringify(mergedUser));
    } catch (error) {
      console.error("Auth bootstrap error:", error);
    } finally {
      bootstrappingRef.current = false;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    localStorage.removeItem("user");
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
